import { PropsWithChildren, ReactNode, RefObject, useRef } from 'react';
import styled from '@emotion/styled';
import { Divider, Tooltip } from '@mui/material';
import { IconButton } from 'components/Buttons/IconButton';
import { useIntl } from 'react-intl';
import { theme } from 'theme';
import { ProductActionsSkeleton } from './ProductActionsSkeleton';
import { ProjectMenuDropdown } from '../../components/ProjectMenuDropdown/ProjectMenuDropdown';
import { definedTranslation } from '../../i18n';

type ProductActionsProps = {
  isLoading: boolean;
  shouldShowContactButton: boolean;
  learnMoreButton: ReactNode;
  productEntityId: string;
  onContactRepClick?: (portalTargetRef: RefObject<HTMLDivElement>) => void;
};

export function ProductActions({
  isLoading,
  productEntityId,
  shouldShowContactButton,
  learnMoreButton,
  children,
  onContactRepClick,
}: PropsWithChildren<ProductActionsProps>) {
  const intl = useIntl();
  const portalTargetRef = useRef<HTMLDivElement | null>(null);

  if (isLoading) {
    return <ProductActionsSkeleton />;
  }

  return (
    <>
      <ActionsWrapper className="flex gap-4" ref={portalTargetRef}>
        <div className="flex items-center gap-4">
          <div>{learnMoreButton}</div>
          {shouldShowContactButton && (
            <TooltipInfo
              PopperProps={{
                disablePortal: true,
              }}
              title={intl?.formatMessage(
                definedTranslation.miniPDP.contactRepButtonLabel,
              )}
            >
              <IconButton
                size="small"
                iconName="mail"
                color="secondary"
                onClick={() => onContactRepClick?.(portalTargetRef)}
              />
            </TooltipInfo>
          )}
          <ProjectSelector>
            <ProjectMenuDropdown
              entityId={productEntityId}
              createProjectButtonLabel={intl?.formatMessage(
                definedTranslation.miniPDP.createProjectButtonLabel,
              )}
            />
          </ProjectSelector>
        </div>
        {children}
      </ActionsWrapper>
      <Divider variant="fullWidth" orientation="horizontal" />
    </>
  );
}

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  width: 100%;
  z-index: 9;
`;

const TooltipInfo = styled(Tooltip)`
  & ~ .MuiTooltip-popper {
    .MuiTooltip-tooltip {
      margin-top: 4px !important;
      background: ${theme.palette.primary.main};
      color: ${theme.palette.common.white};
      font-size: 12px;
      padding: 4px 10px;
      border-radius: 20px;
      text-align: center;
      min-height: 24px;
    }
  }
`;

export const ProjectSelector = styled.div`
  max-width: 200px;
  ${theme.breakpoints.down('lg')} {
    max-width: 250px;
  }
  position: relative;

  & > div:first-of-type {
    position: relative;
    width: auto;
    margin: 0;
    padding: 0;
  }
`;
